import { useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector, runnersActions, legsActions, raceActions, locationActions, uiActions } from '../store/index'
import { AppShell } from '../components/layout/AppShell'
import { runnerService } from '../services/runnerService'
import { legService } from '../services/legService'
import { raceService } from '../services/raceService'
import { authService } from '../services/authService'
import { locationService } from '../services/locationService'
import api from '../services/api'
import type { ActivityUser, ActivityRunner } from '../services/authService'
import { formatPace } from '../utils/paceCalc'
import { formatDuration } from '../utils/formatTime'
import type { DifficultyLevel } from '../types/index'
import { PLACEHOLDER_LEGS } from '../utils/legData'

export function AdminPage() {
  const dispatch = useAppDispatch()
  const runners = useAppSelector(state => state.runners.items)
  const legs = useAppSelector(state => state.legs.assignments)
  const raceStartTime = useAppSelector(state => state.race.raceStartTime)
  const raceStatus = useAppSelector(state => state.race.status)
  const legLogs = useAppSelector(state => state.legs.logs)
  const [activeTab, setActiveTab] = useState<'runners' | 'legs' | 'config' | 'activity' | 'test'>('runners')
  const [activityUsers, setActivityUsers] = useState<ActivityUser[]>([])
  const [activityRunners, setActivityRunners] = useState<ActivityRunner[]>([])
  const [activityLoading, setActivityLoading] = useState(false)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const [newRunnerName, setNewRunnerName] = useState('')
  const [newRunnerNumber, setNewRunnerNumber] = useState<number | null>(null)
  const [raceStarted, setRaceStarted] = useState(false)
  const [selectedRunner, setSelectedRunner] = useState<any | null>(null)
  const [editName, setEditName] = useState('')
  const [editStatus, setEditStatus] = useState('')
  const [newVanNumber, setNewVanNumber] = useState<1 | 2>(1)
  const [editVanNumber, setEditVanNumber] = useState<1 | 2>(1)
  const [editExpectedPace, setEditExpectedPace] = useState<number | null>(null)
  const [selectedLeg, setSelectedLeg] = useState<any | null>(null)
  const [editRunnerId, setEditRunnerId] = useState<number | null>(null)
  const [editDistanceMiles, setEditDistanceMiles] = useState<number | null>(null)
  const [editDifficultyRating, setEditDifficultyRating] = useState<DifficultyLevel | null>(null)
  const [editElevationGain, setEditElevationGain] = useState<number | null>(null)
  const [editElevationLoss, setEditElevationLoss] = useState<number | null>(null)
  const [editStartAddress, setEditStartAddress] = useState('')
  const [editStartLat, setEditStartLat] = useState<number | null>(null)
  const [editStartLng, setEditStartLng] = useState<number | null>(null)
  const [editEndAddress, setEditEndAddress] = useState('')
  const [editEndLat, setEditEndLat] = useState<number | null>(null)
  const [editEndLng, setEditEndLng] = useState<number | null>(null)

  // Test tab state
  const testGlowActive = useAppSelector(state => state.ui.testGlowActive)
  const testPaceMultiplier = useAppSelector(state => state.ui.testPaceMultiplier)
  const currentRunnerId = useAppSelector(state => state.race.currentRunnerId)
  const [smsTo, setSmsTo] = useState('')
  const [smsMessage, setSmsMessage] = useState('Test message from Relay Tracker')
  const [smsStatus, setSmsStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')
  const [spoofLegNumber, setSpoofLegNumber] = useState(1)
  const [spoofRunnerId, setSpoofRunnerId] = useState<number | null>(null)
  const [spoofStatus, setSpoofStatus] = useState<string | null>(null)

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        const [runners, legs, raceState, legLogs] = await Promise.all([
          runnerService.getRunners(),
          legService.getLegs(),
          raceService.getRaceState(),
          legService.getLegLogs(),
        ])
        dispatch(runnersActions.setRunners(runners))
        dispatch(legsActions.setLegAssignments(legs))
        dispatch(legsActions.setLegLogs(legLogs))
        dispatch(raceActions.setRaceStartTime(raceState.raceStartTime))
        setRaceStarted(raceState.raceStatus !== 'not_started')

        // Load cumulative van times from server
        dispatch(raceActions.setCumulativeVan1Time(raceState.cumulativeVan1Time))
        dispatch(raceActions.setCumulativeVan2Time(raceState.cumulativeVan2Time))
      } catch (error) {
        console.error('Failed to load admin data:', error)
      }
    }

    loadData()
  }, [dispatch])

  // Calculate elapsed time from raceStartTime; freeze when race is completed
  useEffect(() => {
    if (!raceStartTime) {
      setElapsedSeconds(0)
      return
    }

    if (raceStatus === 'completed') {
      const leg36Log = legLogs.find(l => l.legNumber === 36 && l.endTime)
      const endMs = leg36Log?.endTime ? new Date(leg36Log.endTime).getTime() : Date.now()
      const diffSeconds = Math.max(0, Math.floor((endMs - new Date(raceStartTime).getTime()) / 1000))
      setElapsedSeconds(diffSeconds)
      return
    }

    const updateElapsed = () => {
      const startTime = new Date(raceStartTime).getTime()
      const diffSeconds = Math.max(0, Math.floor((Date.now() - startTime) / 1000))
      setElapsedSeconds(diffSeconds)
    }

    updateElapsed()
    const interval = setInterval(updateElapsed, 1000)

    return () => clearInterval(interval)
  }, [raceStartTime, raceStatus, legLogs])

  // Poll race state every 5 seconds to stay in sync with server
  useEffect(() => {
    const intervalId = setInterval(async () => {
      try {
        const raceState = await raceService.getRaceState()
        dispatch(raceActions.setCurrentRunner(raceState.currentRunnerId))
        dispatch(legsActions.setCurrentLegNumber(raceState.currentLegNumber))
        dispatch(raceActions.setRaceStartTime(raceState.raceStartTime))
        dispatch(raceActions.setRaceStatus(raceState.raceStatus))
        if (raceState.legStartTime && raceState.raceStatus === 'in_progress') {
          dispatch(raceActions.startTimer(raceState.legStartTime))
        }
      } catch {
        // Silent fail
      }
    }, 5000)
    return () => clearInterval(intervalId)
  }, [dispatch])

  // Fetch activity data when tab is opened
  useEffect(() => {
    if (activeTab !== 'activity') return
    setActivityLoading(true)
    authService.getActivity()
      .then(data => { setActivityUsers(data.users); setActivityRunners(data.runners) })
      .catch(console.error)
      .finally(() => setActivityLoading(false))
  }, [activeTab])

  // Handle add runner
  const handleAddRunner = async () => {
    if (!newRunnerName.trim() || newRunnerNumber === null) {
      alert('Please enter runner name and select a runner number')
      return
    }

    try {
      const newRunner = await runnerService.createRunner({
        name: newRunnerName,
        vanNumber: newVanNumber,
        runnerNumber: newRunnerNumber,
        status: 'resting',
      })
      dispatch(runnersActions.addRunner(newRunner))
      setNewRunnerName('')
      setNewRunnerNumber(null)
    } catch (error) {
      console.error('Failed to add runner:', error)
      alert(`Error: ${error instanceof Error ? error.message : 'Failed to add runner'}`)
    }
  }

  // Handle delete runner
  const handleDeleteRunner = async (id: number) => {
    try {
      await runnerService.deleteRunner(id)
      dispatch(runnersActions.deleteRunner(id))
      setSelectedRunner(null)
    } catch (error) {
      console.error('Failed to delete runner:', error)
    }
  }

  // Handle update runner
  const handleUpdateRunner = async () => {
    if (!selectedRunner) return
    try {
      const updated = await runnerService.updateRunner(selectedRunner.id, {
        name: editName || selectedRunner.name,
        vanNumber: editVanNumber || selectedRunner.vanNumber,
        status: editStatus || selectedRunner.status,
        expectedPace: editExpectedPace,
      })
      dispatch(runnersActions.updateRunner(updated))
      setSelectedRunner(null)
    } catch (error) {
      console.error('Failed to update runner:', error)
      alert(`Error: ${error instanceof Error ? error.message : 'Failed to update runner'}`)
    }
  }

  // Open runner details
  const openRunnerDetails = (runner: any) => {
    setSelectedRunner(runner)
    setEditName(runner.name)
    setEditVanNumber(runner.vanNumber)
    setEditStatus(runner.status)
    setEditExpectedPace(runner.expectedPace)
  }

  // Open leg details
  const openLegDetails = (leg: any) => {
    setSelectedLeg(leg)
    setEditRunnerId(leg.runnerId)
    setEditDistanceMiles(leg.distanceMiles)
    setEditDifficultyRating(leg.difficultyRating)
    setEditElevationGain(leg.elevationGain)
    setEditElevationLoss(leg.elevationLoss)
    setEditStartAddress(leg.startAddress || '')
    setEditStartLat(leg.startLat)
    setEditStartLng(leg.startLng)
    setEditEndAddress(leg.endAddress || '')
    setEditEndLat(leg.endLat)
    setEditEndLng(leg.endLng)
  }

  // Handle update leg
  const handleUpdateLeg = async () => {
    if (!selectedLeg) return
    try {
      const updated = await legService.updateLeg(selectedLeg.id, {
        runnerId: editRunnerId,
        startAddress: editStartAddress,
        startLat: editStartLat ?? undefined,
        startLng: editStartLng ?? undefined,
        endAddress: editEndAddress,
        endLat: editEndLat ?? undefined,
        endLng: editEndLng ?? undefined,
        distanceMiles: editDistanceMiles ?? undefined,
        difficultyRating: editDifficultyRating ?? undefined,
        elevationGain: editElevationGain ?? undefined,
        elevationLoss: editElevationLoss ?? undefined,
      })
      dispatch(legsActions.updateLegAssignment(updated))
      setSelectedLeg(null)
    } catch (error) {
      console.error('Failed to update leg:', error)
      alert(`Error: ${error instanceof Error ? error.message : 'Failed to update leg'}`)
    }
  }

  // Handle start race
  const handleStartRace = async () => {
    try {
      const serverTimeResponse = await authService.getServerTime()
      const serverTime = serverTimeResponse.timestamp

      // Set runner 1 to running status FIRST (before updating race state)
      const runner1 = runners.find(r => r.runnerNumber === 1)
      if (!runner1) {
        throw new Error('Runner #1 not found')
      }

      console.log('🔄 Setting runner #1 to running...')
      try {
        await runnerService.updateRunner(runner1.id, {
          name: runner1.name,
          vanNumber: runner1.vanNumber,
          status: 'running',
          expectedPace: runner1.expectedPace,
        })
        console.log('✅ Runner #1 successfully set to running on server')
      } catch (err: any) {
        const errorMsg = err.response?.data?.error || err.message
        console.error('❌ Failed to set runner #1 to running:', errorMsg)
        throw new Error(`Failed to set runner #1 to running: ${errorMsg}`)
      }
      dispatch(runnersActions.updateRunner({ ...runner1, status: 'running' }))

      // Create leg log for leg 1 with start time
      const leg1 = legs.find(l => l.legNumber === 1)
      if (leg1) {
        console.log('📝 Creating leg log for leg 1...')
        await legService.createLegLog({
          legAssignmentId: leg1.id,
          startTime: serverTime,
          endTime: null,
          actualDurationSeconds: null,
          actualPacePerMile: null,
          notes: null,
        })
        const updatedLeg1 = await legService.updateLeg(leg1.id, { runnerId: runner1.id })
        dispatch(legsActions.updateLegAssignment(updatedLeg1))
        console.log('✅ Leg log created, reloading all logs...')
        // Reload all leg logs to get denormalized fields
        const freshLegLogs = await legService.getLegLogs()
        console.log('📊 Fresh leg logs loaded:', freshLegLogs.length, 'logs')
        dispatch(legsActions.setLegLogs(freshLegLogs))
      }

      // Update race state with start time, leg time, and runner
      await raceService.updateRaceState({
        raceStatus: 'in_progress',
        raceStartTime: serverTime,
        legStartTime: serverTime,
        currentLegNumber: 1,
        currentRunnerId: runner1.id,
      })

      // Update Redux
      dispatch(raceActions.setRaceStartTime(serverTime))
      dispatch(raceActions.setRaceStatus('in_progress'))
      dispatch(raceActions.setCurrentRunner(runner1.id))
      dispatch(legsActions.setCurrentLegNumber(1))
      dispatch(raceActions.setCurrentLeg(1))

      setRaceStarted(true)
    } catch (error) {
      console.error('Failed to start race:', error)
      alert(`Error: ${error instanceof Error ? error.message : 'Failed to start race'}`)
    }
  }

  // Handle reset race
  const handleResetRace = async () => {
    if (!window.confirm('Are you sure you want to reset the race? This will reset all timers and leg progress.')) {
      return
    }
    try {
      await raceService.resetRace()

      // Set all runners to resting status
      for (const runner of runners) {
        await runnerService.updateRunner(runner.id, {
          name: runner.name,
          vanNumber: runner.vanNumber,
          status: 'resting',
          expectedPace: runner.expectedPace,
        })
        dispatch(runnersActions.updateRunner({ ...runner, status: 'resting', averagePace: null }))
      }

      // Reload authoritative state from server to sync all Redux slices
      const freshState = await raceService.getRaceState()
      dispatch(raceActions.setRaceStartTime(freshState.raceStartTime))
      dispatch(raceActions.setRaceStatus(freshState.raceStatus))
      dispatch(raceActions.setCurrentRunner(freshState.currentRunnerId))
      dispatch(legsActions.setCurrentLegNumber(freshState.currentLegNumber))
      dispatch(raceActions.setCumulativeVan1Time(0))
      dispatch(raceActions.setCumulativeVan2Time(0))
      dispatch(legsActions.setLegLogs([]))
      dispatch(locationActions.clearAllLocations())

      setRaceStarted(false)
      alert('Race reset successfully!')
    } catch (error) {
      console.error('Failed to reset race:', error)
      alert(`Error: ${error instanceof Error ? error.message : 'Failed to reset race'}`)
    }
  }

  // Handle logout all users
  const handleLogoutAll = async () => {
    if (!window.confirm('This will log out all connected users. They will need to log in again. Continue?')) return
    try {
      await authService.logoutAll()
      alert('All users have been logged out.')
    } catch (error) {
      alert(`Error: ${error instanceof Error ? error.message : 'Failed to log out users'}`)
    }
  }

  return (
    <AppShell>
      <div className="h-full overflow-y-auto p-3 space-y-3 pb-24">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold flex items-center gap-2">
            <svg className="w-6 h-6 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Admin Panel
          </h1>
          <button
            onClick={handleLogoutAll}
            className="px-3 py-1.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-semibold flex items-center gap-1.5 text-sm"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
            Log Out All
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-1 border-b border-gray-300 dark:border-gray-600">
          <button
            onClick={() => setActiveTab('runners')}
            className={`px-3 py-1.5 text-sm font-medium ${
              activeTab === 'runners'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
            }`}
          >
            <svg className="w-4 h-4 inline-block mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
            Runners
          </button>
          <button
            onClick={() => setActiveTab('legs')}
            className={`px-3 py-1.5 text-sm font-medium ${
              activeTab === 'legs'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
            }`}
          >
            <svg className="w-4 h-4 inline-block mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" /></svg>
            Legs
          </button>
          <button
            onClick={() => setActiveTab('config')}
            className={`px-3 py-1.5 text-sm font-medium ${
              activeTab === 'config'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
            }`}
          >
            <svg className="w-4 h-4 inline-block mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" /></svg>
            Config
          </button>
          <button
            onClick={() => setActiveTab('activity')}
            className={`px-3 py-1.5 text-sm font-medium ${
              activeTab === 'activity'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
            }`}
          >
            <svg className="w-4 h-4 inline-block mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
            Activity
          </button>
          <button
            onClick={() => setActiveTab('test')}
            className={`px-3 py-1.5 text-sm font-medium ${
              activeTab === 'test'
                ? 'border-b-2 border-orange-500 text-orange-500'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
            }`}
          >
            <svg className="w-4 h-4 inline-block mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18" /></svg>
            Test
          </button>
        </div>

        {/* Runners Tab */}
        {activeTab === 'runners' && (
          <div className="space-y-3">
            {/* Add Runner Form */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
              <p className="font-semibold text-sm mb-2">Add New Runner</p>
              <div className="space-y-2">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newRunnerName}
                    onChange={e => setNewRunnerName(e.target.value)}
                    placeholder="Runner name"
                    className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                  />
                  <select
                    value={newVanNumber}
                    onChange={e => setNewVanNumber(parseInt(e.target.value) as 1 | 2)}
                    className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                  >
                    <option value="1">Van 1</option>
                    <option value="2">Van 2</option>
                  </select>
                  <select
                    value={newRunnerNumber ?? ''}
                    onChange={e => setNewRunnerNumber(e.target.value ? parseInt(e.target.value) : null)}
                    className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                  >
                    <option value="">#</option>
                    {Array.from({ length: 12 }, (_, i) => {
                      const runnerNum = i + 1
                      const isUsed = runners.some(r => r.runnerNumber === runnerNum)
                      return (
                        <option key={runnerNum} value={runnerNum} disabled={isUsed}>
                          {runnerNum}{isUsed ? '✓' : ''}
                        </option>
                      )
                    })}
                  </select>
                </div>
                <button
                  onClick={handleAddRunner}
                  className="w-full px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold"
                >
                  <svg className="w-4 h-4 inline-block mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" /></svg>
                  Add Runner
                </button>
              </div>
            </div>

            {/* Runners Table */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-gray-100 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                  <tr>
                    <th className="px-2 py-1.5 text-center font-semibold">#</th>
                    <th className="px-2 py-1.5 text-left font-semibold">Name</th>
                    <th className="px-2 py-1.5 text-center font-semibold">Van</th>
                    <th className="px-2 py-1.5 text-center font-semibold">Status</th>
                    <th className="px-2 py-1.5 text-center font-semibold">Pace</th>
                  </tr>
                </thead>
                <tbody>
                  {runners.map(runner => (
                    <tr
                      key={runner.id}
                      onClick={() => openRunnerDetails(runner)}
                      className={`border-b border-gray-100 dark:border-gray-700 cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/30 ${
                        runner.status === 'running' ? 'bg-green-50 dark:bg-green-950' : ''
                      }`}
                    >
                      <td className="px-2 py-1.5 text-center font-bold">{runner.runnerNumber}</td>
                      <td className="px-2 py-1.5 font-medium">
                        {runner.name}
                      </td>
                      <td className="px-2 py-1.5 text-center">{runner.vanNumber}</td>
                      <td className="px-2 py-1.5 text-center">
                        {runner.status === 'running'
                          ? <svg className="w-4 h-4 mx-auto text-green-700" fill="currentColor" viewBox="0 0 24 24"><path d="M13.49 5.48c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm-3.6 13.9l1-4.4 2.1 2v6h2v-7.5l-2.1-2 .6-3c1.3 1.5 3.3 2.5 5.5 2.5v-2c-1.9 0-3.5-1-4.3-2.4l-1-1.6c-.4-.6-1-1-1.7-1-.3 0-.5.1-.8.1l-5.2 2.2v4.7h2v-3.4l1.8-.7-1.6 8.1-4.9-1-.4 2 7 1.4z"/></svg>
                          : <svg className="w-4 h-4 mx-auto text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" /></svg>}
                      </td>
                      <td className="px-2 py-1.5 text-center font-mono text-gray-500 dark:text-gray-400">
                        {runner.expectedPace ? formatPace(runner.expectedPace) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Runner Detail Modal */}
            {selectedRunner && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full space-y-4">
                  <h3 className="text-xl font-bold">Edit Runner</h3>

                  <div>
                    <label className="block text-sm font-semibold mb-1">Name</label>
                    <input
                      type="text"
                      value={editName}
                      onChange={e => setEditName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-1">Van</label>
                    <select
                      value={editVanNumber}
                      onChange={e => setEditVanNumber(parseInt(e.target.value) as 1 | 2)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                    >
                      <option value="1">Van 1</option>
                      <option value="2">Van 2</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-1">Runner #</label>
                    <p className="text-gray-600 dark:text-gray-400">{selectedRunner.runnerNumber}</p>
                  </div>

                  {selectedRunner.averagePace && (
                    <div>
                      <label className="block text-sm font-semibold mb-1">Average Pace</label>
                      <p className="text-gray-600 dark:text-gray-400">{formatPace(selectedRunner.averagePace)}</p>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-semibold mb-1">Expected Pace</label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        step="0.1"
                        value={editExpectedPace ?? ''}
                        onChange={e => setEditExpectedPace(e.target.value ? parseFloat(e.target.value) : null)}
                        placeholder="e.g., 8.5"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                      />
                      {editExpectedPace && (
                        <div className="flex items-center px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg text-sm font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap">
                          {formatPace(editExpectedPace)}
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-1">Status</label>
                    <select
                      value={editStatus}
                      onChange={e => setEditStatus(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                    >
                      <option value="running">Running</option>
                      <option value="resting">Resting (In Van)</option>
                    </select>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={handleUpdateRunner}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold flex items-center justify-center gap-1.5"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                      Save
                    </button>
                    <button
                      onClick={() => handleDeleteRunner(selectedRunner.id)}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-1.5"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      Delete
                    </button>
                    <button
                      onClick={() => setSelectedRunner(null)}
                      className="px-4 py-2 bg-gray-400 text-white rounded-lg hover:bg-gray-500 flex items-center gap-1.5"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}

            <p className="text-xs text-gray-500 dark:text-gray-400">
              {runners.length} runners — Van 1: {runners.filter(r => r.vanNumber === 1).length}, Van 2: {runners.filter(r => r.vanNumber === 2).length}
            </p>
          </div>
        )}

        {/* Legs Tab */}
        {activeTab === 'legs' && (
          <div className="space-y-3">
            <p className="text-xs text-gray-500 dark:text-gray-400">{legs.length} / 36 legs — tap a row to edit</p>

            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-gray-100 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                  <tr>
                    <th className="px-2 py-1.5 text-center font-semibold">Leg</th>
                    <th className="px-2 py-1.5 text-left font-semibold">Runner</th>
                    <th className="px-2 py-1.5 text-center font-semibold">Dist</th>
                    <th className="px-2 py-1.5 text-center font-semibold">Diff</th>
                    <th className="px-2 py-1.5 text-center font-semibold">↑ft</th>
                    <th className="px-2 py-1.5 text-center font-semibold">↓ft</th>
                  </tr>
                </thead>
                <tbody>
                  {legs.map(leg => (
                    <tr
                      key={leg.id}
                      onClick={() => openLegDetails(leg)}
                      className="border-b border-gray-100 dark:border-gray-700 cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/30"
                    >
                      <td className="px-2 py-1.5 text-center font-bold">{leg.legNumber}</td>
                      <td className="px-2 py-1.5">
                        {runners.find(r => r.id === leg.runnerId)?.name || <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-2 py-1.5 text-center">{leg.distanceMiles?.toFixed(1) || '—'}</td>
                      <td className="px-2 py-1.5 text-center">{leg.difficultyRating || '—'}</td>
                      <td className="px-2 py-1.5 text-center text-gray-500 dark:text-gray-400">{leg.elevationGain ?? '—'}</td>
                      <td className="px-2 py-1.5 text-center text-gray-500 dark:text-gray-400">{leg.elevationLoss ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Leg Detail Modal — bottom sheet */}
            {selectedLeg && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end z-[500]">
                <div className="bg-white dark:bg-gray-800 rounded-t-2xl w-full max-h-[calc(90vh-5rem)] mb-20 flex flex-col">
                  {/* Sticky header + actions */}
                  <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-600 px-4 py-2.5 flex justify-between items-center rounded-t-2xl flex-shrink-0">
                    <h3 className="text-base font-bold">Leg {selectedLeg.legNumber}</h3>
                    <div className="flex gap-2">
                      <button
                        onClick={handleUpdateLeg}
                        className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-semibold flex items-center gap-1"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                        Save
                      </button>
                      <button
                        onClick={() => setSelectedLeg(null)}
                        className="px-3 py-1.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm"
                      >
                        Close
                      </button>
                    </div>
                  </div>

                  {/* Scrollable body */}
                  <div className="overflow-y-auto p-3 space-y-2.5">

                    {/* Row: Distance + Difficulty */}
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-0.5">Distance (mi)</label>
                        <input
                          type="number" step="0.1"
                          value={editDistanceMiles ?? ''}
                          onChange={e => setEditDistanceMiles(e.target.value ? parseFloat(e.target.value) : null)}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-0.5">Difficulty</label>
                        <select
                          value={editDifficultyRating ?? ''}
                          onChange={e => setEditDifficultyRating((e.target.value as DifficultyLevel) || null)}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                        >
                          <option value="">—</option>
                          <option value="E">E — Easy</option>
                          <option value="M">M — Moderate</option>
                          <option value="MC">MC — More Chall.</option>
                          <option value="MD">MD — Most Diff.</option>
                        </select>
                      </div>
                    </div>

                    {/* Row: Elevation Gain + Loss */}
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-0.5">↑ Gain (ft)</label>
                        <input
                          type="number" step="1"
                          value={editElevationGain ?? ''}
                          onChange={e => setEditElevationGain(e.target.value ? parseFloat(e.target.value) : null)}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-0.5">↓ Loss (ft)</label>
                        <input
                          type="number" step="1"
                          value={editElevationLoss ?? ''}
                          onChange={e => setEditElevationLoss(e.target.value ? parseFloat(e.target.value) : null)}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                        />
                      </div>
                    </div>

                    <hr />

                    {/* Start point */}
                    <p className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wide">Start</p>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-0.5">Name / Address</label>
                      <input
                        type="text"
                        value={editStartAddress}
                        onChange={e => setEditStartAddress(e.target.value)}
                        placeholder="e.g., Reno Trailhead"
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-0.5">Lat</label>
                        <input
                          type="number" step="0.0001"
                          value={editStartLat ?? ''}
                          onChange={e => setEditStartLat(e.target.value ? parseFloat(e.target.value) : null)}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-0.5">Lng</label>
                        <input
                          type="number" step="0.0001"
                          value={editStartLng ?? ''}
                          onChange={e => setEditStartLng(e.target.value ? parseFloat(e.target.value) : null)}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                        />
                      </div>
                    </div>

                    {/* End point */}
                    <p className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wide">End</p>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-0.5">Name / Address</label>
                      <input
                        type="text"
                        value={editEndAddress}
                        onChange={e => setEditEndAddress(e.target.value)}
                        placeholder="e.g., Tahoe Summit"
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-0.5">Lat</label>
                        <input
                          type="number" step="0.0001"
                          value={editEndLat ?? ''}
                          onChange={e => setEditEndLat(e.target.value ? parseFloat(e.target.value) : null)}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-0.5">Lng</label>
                        <input
                          type="number" step="0.0001"
                          value={editEndLng ?? ''}
                          onChange={e => setEditEndLng(e.target.value ? parseFloat(e.target.value) : null)}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                        />
                      </div>
                    </div>

                    <hr />

                    {/* Assigned Runner */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-0.5">Assigned Runner</label>
                      <select
                        value={editRunnerId || ''}
                        onChange={e => setEditRunnerId(e.target.value ? parseInt(e.target.value) : null)}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                      >
                        <option value="">Unassigned</option>
                        {runners.map(runner => (
                          <option key={runner.id} value={runner.id}>
                            #{runner.runnerNumber} {runner.name} (Van {runner.vanNumber})
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Bottom safe area */}
                    <div className="h-6 safe-bottom" />
                  </div>
                </div>
              </div>
            )}

            {legs.length < 36 && (
              <div className="bg-yellow-50 dark:bg-yellow-950 border border-yellow-300 dark:border-yellow-700 p-3 rounded text-sm text-yellow-800 dark:text-yellow-200">
                Only {legs.length} legs loaded. Full leg data will sync from server.
              </div>
            )}
          </div>
        )}

        {/* Config Tab */}
        {activeTab === 'config' && (
          <div className="space-y-4">
            <h2 className="text-lg font-bold">Race Configuration</h2>

            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-600 space-y-3">
              <div>
                <p className="font-semibold mb-2">Race Status</p>
                <div className="space-y-2">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className={`inline-flex items-center gap-1.5 text-sm font-semibold px-2 py-1 rounded-full ${
                      raceStatus === 'completed' ? 'bg-blue-100 dark:bg-blue-950 text-blue-800 dark:text-blue-200'
                      : raceStatus === 'in_progress' ? 'bg-green-100 dark:bg-green-950 text-green-800 dark:text-green-200'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                    }`}>
                      <span className={`w-2 h-2 rounded-full ${
                        raceStatus === 'completed' ? 'bg-blue-500'
                        : raceStatus === 'in_progress' ? 'bg-green-500'
                        : 'bg-gray-400'
                      }`} />
                      {raceStatus === 'completed' ? 'Completed' : raceStatus === 'in_progress' ? 'In Progress' : 'Not Started'}
                    </span>
                    <div className="flex gap-2">
                      {raceStatus === 'not_started' && (
                        <button
                          onClick={handleStartRace}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold flex items-center gap-1.5"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                          Start Race
                        </button>
                      )}
                      <button
                        onClick={handleResetRace}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold flex items-center gap-1.5"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                        Reset Race
                      </button>
                    </div>
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    <p className="flex items-center gap-1.5">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><circle cx="12" cy="12" r="10"/><path strokeLinecap="round" d="M12 6v6l4 2"/></svg>
                      Total Elapsed: <span className="font-mono font-semibold text-gray-900 dark:text-gray-100">{formatDuration(elapsedSeconds)}</span>
                    </p>
                  </div>
                </div>
              </div>

              <hr />

              <div>
                <p className="font-semibold mb-2">System Info</p>
                <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                  <p className="flex items-center gap-1.5"><svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/></svg> Runners: {runners.length}</p>
                  <p className="flex items-center gap-1.5"><svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"/></svg> Legs: {legs.length}</p>
                  <p className="flex items-center gap-1.5"><svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><circle cx="12" cy="12" r="10"/><path strokeLinecap="round" d="M12 6v6l4 2"/></svg> Server Time: {new Date().toLocaleTimeString('en-US', { timeZone: 'America/Los_Angeles', hour: '2-digit', minute: '2-digit', second: '2-digit' })}</p>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 dark:bg-blue-950 border border-blue-300 dark:border-blue-700 p-4 rounded-lg text-sm text-blue-900 dark:text-blue-200">
              <p className="font-semibold flex items-center gap-1.5">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                Admin Actions
              </p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Start race to enable location tracking</li>
                <li>Stop and reset race at any time</li>
                <li>View all logs and export data</li>
              </ul>
            </div>
          </div>
        )}
        {/* Activity Tab */}
        {activeTab === 'activity' && (
          <div className="space-y-4">
            <h2 className="text-lg font-bold">User Activity</h2>

            {activityLoading ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
            ) : (
              <>
                {/* Named accounts */}
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
                  <div className="bg-gray-100 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 px-3 py-1.5">
                    <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Accounts</p>
                  </div>
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-600">
                      <tr>
                        <th className="px-3 py-1.5 text-left font-semibold">User</th>
                        <th className="px-3 py-1.5 text-left font-semibold">Role</th>
                        <th className="px-3 py-1.5 text-left font-semibold">Last Login</th>
                        <th className="px-3 py-1.5 text-center font-semibold">Devices</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activityUsers.map(u => (
                        <tr key={u.id} className="border-b border-gray-100 dark:border-gray-700 last:border-0">
                          <td className="px-3 py-1.5 font-medium">{u.displayName}</td>
                          <td className="px-3 py-1.5">
                            <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-semibold ${
                              u.role === 'admin' ? 'bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-300'
                              : u.role === 'spectator' ? 'bg-purple-100 dark:bg-purple-950 text-purple-700 dark:text-purple-300'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                            }`}>{u.role}</span>
                          </td>
                          <td className="px-3 py-1.5 text-gray-500 dark:text-gray-400">
                            {u.lastLoginAt
                              ? new Date(u.lastLoginAt).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                              : <span className="text-gray-300">Never</span>}
                          </td>
                          <td className="px-3 py-1.5 text-center">
                            {u.deviceCount > 0
                              ? <span className="inline-flex items-center gap-1 font-mono font-semibold text-blue-700">{u.deviceCount}</span>
                              : <span className="text-gray-300">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Runners */}
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
                  <div className="bg-gray-100 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 px-3 py-1.5">
                    <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Runners</p>
                  </div>
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-600">
                      <tr>
                        <th className="px-3 py-1.5 text-center font-semibold">#</th>
                        <th className="px-3 py-1.5 text-left font-semibold">Name</th>
                        <th className="px-3 py-1.5 text-center font-semibold">Van</th>
                        <th className="px-3 py-1.5 text-left font-semibold">Last Login</th>
                        <th className="px-3 py-1.5 text-center font-semibold">Devices</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activityRunners.map(r => (
                        <tr key={r.id} className="border-b border-gray-100 dark:border-gray-700 last:border-0">
                          <td className="px-3 py-1.5 text-center font-bold">{r.runnerNumber}</td>
                          <td className="px-3 py-1.5 font-medium">{r.displayName}</td>
                          <td className="px-3 py-1.5 text-center text-gray-500 dark:text-gray-400">{r.vanNumber}</td>
                          <td className="px-3 py-1.5 text-gray-500 dark:text-gray-400">
                            {r.lastLoginAt
                              ? new Date(r.lastLoginAt).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                              : <span className="text-gray-300">Never</span>}
                          </td>
                          <td className="px-3 py-1.5 text-center">
                            {r.deviceCount > 0
                              ? <span className="inline-flex items-center gap-1 font-mono font-semibold text-blue-700">{r.deviceCount}</span>
                              : <span className="text-gray-300">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <button
                  onClick={() => {
                    setActivityLoading(true)
                    authService.getActivity()
                      .then(data => { setActivityUsers(data.users); setActivityRunners(data.runners) })
                      .catch(console.error)
                      .finally(() => setActivityLoading(false))
                  }}
                  className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                  Refresh
                </button>
              </>
            )}
          </div>
        )}
        {/* Test Tab */}
        {activeTab === 'test' && (
          <div className="space-y-4">
            <p className="text-xs text-orange-600 dark:text-orange-400 font-semibold uppercase tracking-wide">Admin Test Tools — not visible to runners</p>

            {/* 1. Green Glow Border */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 p-4 space-y-2">
              <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">Green Glow Border</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Simulates the glow that appears on the Map when a runner is within the exchange boundary.</p>
              <button
                onClick={() => dispatch(uiActions.setTestGlow(!testGlowActive))}
                className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                  testGlowActive
                    ? 'bg-green-600 text-white border-green-700'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-500'
                }`}
              >
                {testGlowActive ? '✓ Glow Active — switch to Map tab to see it' : 'Activate Glow'}
              </button>
            </div>

            {/* 2. SMS Test */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 p-4 space-y-2">
              <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">SMS Test</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Send a test SMS via Twilio to verify the messaging system works.</p>
              <input
                type="tel"
                placeholder="Phone number (e.g. 7755551234)"
                value={smsTo}
                onChange={e => setSmsTo(e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-3 py-1.5 text-sm"
              />
              <input
                type="text"
                placeholder="Message"
                value={smsMessage}
                onChange={e => setSmsMessage(e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-3 py-1.5 text-sm"
              />
              <button
                disabled={smsStatus === 'sending' || !smsTo}
                onClick={async () => {
                  setSmsStatus('sending')
                  try {
                    await api.post('/test/sms', { to: smsTo, message: smsMessage })
                    setSmsStatus('sent')
                    setTimeout(() => setSmsStatus('idle'), 4000)
                  } catch {
                    setSmsStatus('error')
                    setTimeout(() => setSmsStatus('idle'), 4000)
                  }
                }}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  smsStatus === 'sent' ? 'bg-green-600 text-white' :
                  smsStatus === 'error' ? 'bg-red-600 text-white' :
                  'bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50'
                }`}
              >
                {smsStatus === 'sending' ? 'Sending…' : smsStatus === 'sent' ? '✓ Sent!' : smsStatus === 'error' ? '✗ Failed' : 'Send SMS'}
              </button>
            </div>

            {/* 3. Pace Multiplier */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 p-4 space-y-2">
              <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">Pace Multiplier</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Speed up runner position projections on the map for simulation testing. Does not affect actual pace data.</p>
              <div className="flex gap-2">
                {([1, 2, 3, 4] as const).map(m => (
                  <button
                    key={m}
                    onClick={() => dispatch(uiActions.setTestPaceMultiplier(m))}
                    className={`flex-1 py-2 rounded-lg text-sm font-bold border transition-colors ${
                      testPaceMultiplier === m
                        ? 'bg-blue-600 text-white border-blue-700'
                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-500'
                    }`}
                  >
                    {m}×
                  </button>
                ))}
              </div>
              {testPaceMultiplier > 1 && (
                <p className="text-xs text-orange-600 dark:text-orange-400">Projections running at {testPaceMultiplier}× speed</p>
              )}
            </div>

            {/* 4. Spoof Runner Location */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 p-4 space-y-2">
              <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">Spoof Runner Location</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Teleports any runner to the start of any exchange point. Useful for testing proximity triggers.</p>
              <select
                value={spoofRunnerId ?? ''}
                onChange={e => setSpoofRunnerId(e.target.value ? Number(e.target.value) : null)}
                className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-3 py-1.5 text-sm"
              >
                <option value="">Select a runner…</option>
                {runners.map(r => (
                  <option key={r.id} value={r.id}>
                    Runner {r.runnerNumber} — {r.name} (Van {r.vanNumber})
                  </option>
                ))}
              </select>
              <select
                value={spoofLegNumber}
                onChange={e => setSpoofLegNumber(Number(e.target.value))}
                className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-3 py-1.5 text-sm"
              >
                {PLACEHOLDER_LEGS.map(leg => (
                  <option key={leg.legNumber} value={leg.legNumber}>
                    XP {leg.legNumber} — Leg {leg.legNumber} Start ({leg.startLat?.toFixed(4)}, {leg.startLng?.toFixed(4)})
                  </option>
                ))}
              </select>
              <button
                disabled={!spoofRunnerId}
                onClick={async () => {
                  const leg = PLACEHOLDER_LEGS.find(l => l.legNumber === spoofLegNumber)
                  if (!leg || !spoofRunnerId) return
                  const selectedRunner = runners.find(r => r.id === spoofRunnerId)
                  const loc = { runnerId: spoofRunnerId, latitude: leg.startLat, longitude: leg.startLng, accuracy: 5, timestamp: new Date().toISOString() }
                  dispatch(locationActions.updateLocation({ runnerId: spoofRunnerId, location: { latitude: leg.startLat, longitude: leg.startLng, accuracy: 5, timestamp: loc.timestamp } }))
                  try {
                    await locationService.postLocation(loc)
                    setSpoofStatus(`${selectedRunner?.name ?? 'Runner'} spoofed to XP ${spoofLegNumber}`)
                  } catch {
                    setSpoofStatus('Posted to Redux only (server error)')
                  }
                  setTimeout(() => setSpoofStatus(null), 4000)
                }}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-orange-600 text-white hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Teleport {spoofRunnerId ? (runners.find(r => r.id === spoofRunnerId)?.name ?? 'Runner') : '…'} to XP {spoofLegNumber}
              </button>
              {spoofStatus && <p className="text-xs text-green-600 dark:text-green-400">{spoofStatus}</p>}
            </div>
          </div>
        )}

        {/* App Version */}
        <div className="mt-6 pb-4 text-center text-xs text-gray-400 dark:text-gray-600">
          v{__APP_VERSION__} &middot; Built {new Date(__BUILD_TIME__).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
        </div>

      </div>
    </AppShell>
  )
}
